import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';

export default function UserGroupManager() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [isNewGroupOpen, setIsNewGroupOpen] = useState(false);
  const [isEditGroupOpen, setIsEditGroupOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [isUserSearchOpen, setIsUserSearchOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    filters: {
      status: 'new',
      registrationDateFrom: '',
      registrationDateTo: '',
      city: '',
      country: ''
    },
    users: []
  });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'userGroups'));
      const groupsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGroups(groupsData);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Hiba történt a csoportok betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedGroup) {
        await updateDoc(doc(db, 'userGroups', selectedGroup.id), {
          ...formData,
          updatedAt: new Date()
        });
        toast.success('Csoport sikeresen frissítve');
      } else {
        await addDoc(collection(db, 'userGroups'), {
          ...formData,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        toast.success('Új csoport sikeresen létrehozva');
      }
      fetchGroups();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving group:', error);
      toast.error('Hiba történt a csoport mentésekor');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await deleteDoc(doc(db, 'userGroups', groupId));
      toast.success('Csoport sikeresen törölve');
      fetchGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Hiba történt a csoport törlésekor');
    }
  };

  const handleCloseModal = () => {
    setIsNewGroupOpen(false);
    setIsEditGroupOpen(false);
    setSelectedGroup(null);
    setFormData({
      name: '',
      description: '',
      filters: {
        status: 'new',
        registrationDateFrom: '',
        registrationDateTo: '',
        city: '',
        country: ''
      },
      users: []
    });
  };

  const searchUsers = async (term) => {
    if (term.length < 2) return;

    try {
      const usersRef = collection(db, 'customers');
      // Először próbáljuk customerId alapján
      const querySnapshot = await getDocs(
        query(
          usersRef,
          where('customerId', '>=', term.toLowerCase()),
          where('customerId', '<=', term.toLowerCase() + '\uf8ff'),
          limit(10)
        )
      );

      let users = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Ha nincs találat, próbáljuk az email alapján
      if (users.length === 0) {
        const emailSnapshot = await getDocs(
          query(
            usersRef,
            where('primaryContact.email', '>=', term.toLowerCase()),
            where('primaryContact.email', '<=', term.toLowerCase() + '\uf8ff'),
            limit(10)
          )
        );

        users = emailSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }

      // Ha még mindig nincs találat, próbáljuk a név alapján
      if (users.length === 0) {
        const nameSnapshot = await getDocs(
          query(
            usersRef,
            where('primaryContact.lastName', '>=', term),
            where('primaryContact.lastName', '<=', term + '\uf8ff'),
            limit(10)
          )
        );

        users = nameSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }

      setUserSearchResults(users);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Hiba történt a felhasználók keresésekor');
    }
  };

  const handleAddUser = (user) => {
    if (!formData.users.find(u => u.id === user.id)) {
      setFormData({
        ...formData,
        users: [...formData.users, user]
      });
    }
    setUserSearchResults([]);
    setSearchTerm('');
  };

  const handleRemoveUser = (userId) => {
    setFormData({
      ...formData,
      users: formData.users.filter(u => u.id !== userId)
    });
  };

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Felhasználói Csoportok</h2>
          <button
            onClick={() => setIsNewGroupOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <Icons.Plus className="h-4 w-4 mr-2" />
            Új csoport
          </button>
        </div>

        {/* Groups Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {groups.map((group) => (
            <div
              key={group.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{group.name}</h3>
                  <p className="text-sm text-gray-500">{group.description}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setSelectedGroup(group);
                      setFormData({
                        name: group.name,
                        description: group.description,
                        filters: group.filters || {},
                        users: group.users || []
                      });
                      setIsEditGroupOpen(true);
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <Icons.Edit2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteGroup(group.id)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <Icons.Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              {/* Filters */}
              {group.filters && Object.keys(group.filters).length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Szűrők:</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(group.filters).map(([key, value]) => 
                      value && (
                        <span
                          key={key}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {key}: {value}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Modal */}
        <Dialog
          open={isNewGroupOpen || isEditGroupOpen}
          onClose={handleCloseModal}
          className="relative z-50"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="mx-auto max-w-2xl w-full bg-white rounded-xl shadow-lg">
              <div className="flex justify-between items-center px-6 py-4 border-b">
                <Dialog.Title className="text-lg font-medium">
                  {isEditGroupOpen ? 'Csoport szerkesztése' : 'Új csoport létrehozása'}
                </Dialog.Title>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <Icons.X className="h-5 w-5" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Csoport neve
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Leírás
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                {/* Szűrők */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-700">Automatikus szűrők</h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Státusz
                      </label>
                      <select
                        value={formData.filters.status}
                        onChange={(e) => setFormData({
                          ...formData,
                          filters: { ...formData.filters, status: e.target.value }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="new">Új ügyfelek</option>
                        <option value="active">Aktív ügyfelek</option>
                        <option value="inactive">Inaktív ügyfelek</option>
                        <option value="all">Összes ügyfél</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Regisztráció dátuma (ettől)
                      </label>
                      <input
                        type="date"
                        value={formData.filters.registrationDateFrom}
                        onChange={(e) => setFormData({
                          ...formData,
                          filters: { ...formData.filters, registrationDateFrom: e.target.value }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Manuális felhasználó hozzáadás */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-700">Felhasználók manuális hozzáadása</h4>
                  
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        searchUsers(e.target.value);
                      }}
                      placeholder="Felhasználó keresése..."
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                    
                    {/* Keresési eredmények */}
                    {userSearchResults.length > 0 ? (
                      <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-auto">
                        {userSearchResults.map(user => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => handleAddUser(user)}
                            className="w-full text-left px-4 py-2 hover:bg-gray-50"
                          >
                            <div className="font-medium">
                              {user.customerId} - {user.primaryContact?.firstName} {user.primaryContact?.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.primaryContact?.email}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : searchTerm.length >= 2 && (
                      <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-md border border-gray-200 p-4 text-center text-gray-500">
                        Nincs találat
                      </div>
                    )}
                  </div>

                  {/* Kiválasztott felhasználók listája */}
                  {formData.users.length > 0 && (
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Kiválasztott felhasználók</h5>
                      <div className="space-y-2">
                        {formData.users.map(user => (
                          <div
                            key={user.id}
                            className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md"
                          >
                            <div>
                              <div className="font-medium">{user.name}</div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveUser(user.id)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <Icons.X className="h-5 w-5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Mégse
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                  >
                    {isEditGroupOpen ? 'Mentés' : 'Létrehozás'}
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </div>
        </Dialog>
      </div>
    </div>
  );
} 